import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "routing"
    }}>{`Routing`}</h1>
    <p>{`For web applications, routing is the process of mapping a URL to specific page resources.`}</p>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`In a multi-page application, routing is performed on the server side.
Every URL request fetches new HTML from the server and the browser loads the entire page.
This approach is inefficient because the same assets get loaded every time site navigation occurs.`}</p>
    <p>{`For a single-page application (SPA), such as a progressive web app, routing is performed on the client side.
Single-page applications do not reload the browser during navigation.
Instead, the application uses the URL to fetch smaller pieces of data from the server and updates specific items on the page.`}</p>
    <h2 {...{
      "id": "routing-for-pwa-studio-storefronts"
    }}>{`Routing for PWA Studio storefronts`}</h2>
    <p>{`PWA Studio provides tools that support both server-side and client-side routing.`}</p>
    <h3 {...{
      "id": "server-side-routing"
    }}>{`Server-side routing`}</h3>
    <p>{`Server-side routing is accomplished using the `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/upward/"
      }}>{`UPWARD`}</a>{` configuration file.
Since the configuration file defines how the server responds to requests,
you can specify a different template to render each page type, such as a CMS page or a product details page.`}</p>
    <p>{`Early versions of the Venia storefront used this approach, but
in the current version, every page request now returns the same HTML with the application shell.
The application decides how the page should render based on the request.`}</p>
    <h3 {...{
      "id": "client-side-routing"
    }}>{`Client-side routing`}</h3>
    <p>{`Client-side routing happens inside the storefront application.
When a user navigates inside the application, it updates the relevant pieces instead of refreshing the entire page to update content.`}</p>
    <p>{`Since Venia is a single-page application, it uses client-side routing for internal navigation.`}</p>
    <h2 {...{
      "id": "how-routing-works-in-venia"
    }}>{`How routing works in Venia`}</h2>
    <p>{`This section goes over the routing flow implemented in Venia.
It is the default workflow for all new projects created using the scaffolding tool,
but it is not the only possible workflow for routing.`}</p>
    <h3 {...{
      "id": "initial-request"
    }}>{`Initial request`}</h3>
    <p>{`Venia's UPWARD server handles the initial request to the storefront application.
Its `}<inlineCode parentName="p">{`upward.yml`}</inlineCode>{` configuration tells the server to return an `}<inlineCode parentName="p">{`index.html`}</inlineCode>{` page created at build time by Webpack, via the `}<a parentName="p" {...{
        "href": "https://webpack.js.org/plugins/html-webpack-plugin/"
      }}>{`HtmlWebpackPlugin`}</a>{`.
The content of this file is the same for all page types.`}</p>
    <p>{`After the browser loads the application shell, routing is handled client-side by React Router.`}</p>
    <h3 {...{
      "id": "routes-component"
    }}>{`Routes component`}</h3>
    <p>{`Used inside the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/venia-ui/lib/components/App/app.js"
      }}>{`App component`}</a>{`, the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/venia-ui/lib/components/Routes/routes.js"
      }}>{`Routes component`}</a>{` provides the switch logic for deciding which component to use to render the main content for the current route.`}</p>
    <p>{`Routes in a PWA do not have to correspond to routes in the Adobe Commerce or Magento Open Source application backend.
For example, Venia has routes not defined in the backend, such as `}<em parentName="p">{`Create account`}</em>{` or full page `}<em parentName="p">{`Checkout`}</em>{`.
These routes are assigned components which render content for those pages in the Routes component.`}</p>
    <h3 {...{
      "id": "magentoroute-component"
    }}>{`MagentoRoute component`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/venia-ui/lib/components/MagentoRoute/magentoRoute.js"
      }}>{`MagentoRoute component`}</a>{` handles the routes that are specific to the Adobe Commerce or Magento Open Source application, such as a product or category page.
It uses its `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/peregrine/"
      }}>{`Peregrine talon`}</a>{` counterpart to determine which component to display.`}</p>
    <h3 {...{
      "id": "usemagentoroute-talon"
    }}>{`useMagentoRoute() talon`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/peregrine/lib/talons/MagentoRoute/useMagentoRoute.js"
      }}><inlineCode parentName="a">{`useMagentoRoute()`}</inlineCode></a>{` talon returns the correct component for a page type. It uses the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/peregrine/lib/talons/MagentoRoute/helpers.js"
      }}><inlineCode parentName="a">{`getRootComponent()`}</inlineCode></a>{` helper function to get the `}<strong parentName="p">{`root component`}</strong>{` associated with a page type.
It uses the `}<inlineCode parentName="p">{`resolveUnknownRoute()`}</inlineCode>{` function to determine the page type for a route and retrieves the root component associated with that type using a global `}<inlineCode parentName="p">{`fetchRootComponent`}</inlineCode>{` function.
The `}<inlineCode parentName="p">{`fetchRootComponent`}</inlineCode>{` function is autogenerated at build time by the Buildpack RootComponentsPlugin, which searches for RootComponents in the project and builds a manifest.`}</p>
    <h3 {...{
      "id": "resolveunknownroute"
    }}>{`resolveUnknownRoute()`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/peregrine/lib/Router/resolveUnknownRoute.js"
      }}><inlineCode parentName="a">{`resolveUnknownRoute()`}</inlineCode></a>{` function is a utility function for fetching page type information from the backend server using a GraphQL query.
The `}<inlineCode parentName="p">{`getRouteComponent()`}</inlineCode>{` function uses the information from this query to get the correct root component from an object that maps page types to root components.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      